/**
 * DetectionViewer Component
 *
 * Enterprise-grade waveform-based detection viewer for inference results.
 * Displays audio waveform with detection regions and a list of detections.
 */

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner,
} from '@heroui/react';
import { FileAudio, X, Keyboard } from 'lucide-react';

import { useInferenceFile, useInferenceFileDownloadUrl } from '@/lib/api/hooks/inference';
import type { Detection } from '@/api';

import { WaveformPlayer, type WaveformPlayerRef } from './WaveformPlayer';
import { PlaybackControls } from './PlaybackControls';
import { DetectionList } from './DetectionList';

interface ArtifactType {
  name: string;
  color: string;
}

interface DetectionViewerProps {
  jobId: string;
  fileId: string;
  artifactTypes: ArtifactType[];
  onClose?: () => void;
}

// Default colors for artifact types
const DEFAULT_COLORS: Record<string, string> = {
  glitch: '#ef4444', // red-500
  pause: '#3b82f6', // blue-500
  hallucination: '#a855f7', // purple-500
  clipping: '#f59e0b', // amber-500
  noise: '#6b7280', // gray-500
  silence: '#06b6d4', // cyan-500
  default: '#6366f1', // indigo-500
};

export function DetectionViewer({
  jobId,
  fileId,
  artifactTypes,
  onClose,
}: DetectionViewerProps) {
  const waveformRef = useRef<WaveformPlayerRef>(null);
  const zoomRef = useRef(50);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [volume, setVolume] = useState(1);
  const [zoomLevel, setZoomLevel] = useState(50); // Only for UI display, not for triggering re-renders

  // Detection state
  const [activeDetection, setActiveDetection] = useState<Detection | null>(null);
  const [hoveredDetection, setHoveredDetection] = useState<Detection | null>(null);

  // Fetch file data and download URL
  const { data: file, isLoading: isLoadingFile } = useInferenceFile(jobId, fileId);
  const { data: downloadData, isLoading: isLoadingUrl } = useInferenceFileDownloadUrl(
    jobId,
    fileId
  );

  const detections = useMemo(() => file?.detections ?? [], [file?.detections]);

  // Get color for artifact type
  const getColorForType = useCallback(
    (type: string) => {
      const found = artifactTypes.find((t) => t.name.toLowerCase() === type.toLowerCase());
      if (found?.color) return found.color;
      return DEFAULT_COLORS[type.toLowerCase()] || DEFAULT_COLORS.default;
    },
    [artifactTypes]
  );

  // Handle detection click - seek to detection start
  const handleDetectionClick = useCallback((detection: Detection) => {
    setActiveDetection(detection);
    waveformRef.current?.seekTo(detection.start_ms / 1000);
  }, []);

  // Handle playback rate change
  const handlePlaybackRateChange = useCallback((rate: number) => {
    setPlaybackRate(rate);
    waveformRef.current?.setPlaybackRate(rate);
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((vol: number) => {
    setVolume(vol);
    waveformRef.current?.setVolume(vol);
  }, []);

  // Handle zoom change - use ref to avoid re-renders, update state with requestAnimationFrame
  const handleZoomChange = useCallback((z: number) => {
    zoomRef.current = z;
    waveformRef.current?.zoom(z);
    // Batch state update to avoid flicker
    requestAnimationFrame(() => {
      setZoomLevel(z);
    });
  }, []);

  // Handle seek
  const handleSeek = useCallback((time: number) => {
    waveformRef.current?.seekTo(time);
  }, []);

  // Navigate to next/previous detection
  const navigateDetection = useCallback(
    (direction: 'next' | 'prev') => {
      if (detections.length === 0) return;

      const currentIndex = activeDetection
        ? detections.findIndex(
            (d) =>
              d.start_ms === activeDetection.start_ms &&
              d.end_ms === activeDetection.end_ms &&
              d.artifact_type === activeDetection.artifact_type
          )
        : -1;

      let newIndex: number;
      if (direction === 'next') {
        newIndex = currentIndex < detections.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : detections.length - 1;
      }

      const newDetection = detections[newIndex];
      setActiveDetection(newDetection);
      waveformRef.current?.seekTo(newDetection.start_ms / 1000);
    },
    [detections, activeDetection]
  );

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ignore if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      switch (e.key) {
        case ' ':
          e.preventDefault();
          waveformRef.current?.playPause();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          waveformRef.current?.seekTo(Math.max(0, currentTime - 5));
          break;
        case 'ArrowRight':
          e.preventDefault();
          waveformRef.current?.seekTo(Math.min(duration, currentTime + 5));
          break;
        case 'ArrowUp':
          e.preventDefault();
          navigateDetection('prev');
          break;
        case 'ArrowDown':
          e.preventDefault();
          navigateDetection('next');
          break;
        case '[':
          e.preventDefault();
          handleZoomChange(Math.max(20, zoomRef.current - 50));
          break;
        case ']':
          e.preventDefault();
          handleZoomChange(Math.min(500, zoomRef.current + 50));
          break;
        case 'Escape':
          e.preventDefault();
          onClose?.();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentTime, duration, navigateDetection, handleZoomChange, onClose]);

  // Combined hover state (from list or region)
  const displayedActiveDetection = hoveredDetection || activeDetection;

  // Loading state
  if (isLoadingFile || isLoadingUrl) {
    return (
      <div className="flex flex-col h-full">
        <ViewerHeader
          filename={file?.original_filename}
          detectionCount={0}
          onClose={onClose}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <Spinner size="lg" />
            <span className="text-sm text-default-500">Loading audio...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (!file) {
    return (
      <div className="flex flex-col h-full">
        <ViewerHeader filename={undefined} detectionCount={0} onClose={onClose} />
        <div className="flex-1 flex items-center justify-center">
          <Card className="border border-danger-100 shadow-none max-w-md">
            <CardBody className="text-center py-8">
              <p className="text-danger font-medium">Failed to load file</p>
              <p className="text-sm text-default-500 mt-2">
                The file may have been deleted or you may not have permission to view it.
              </p>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <ViewerHeader
        filename={file.original_filename}
        detectionCount={detections.length}
        onClose={onClose}
      />

      {/* Main content */}
      <div className="flex-1 overflow-hidden p-4">
        <div className="h-full flex flex-col lg:flex-row gap-4">
          {/* Waveform section */}
          <div className="flex-1 flex flex-col gap-4">
            {/* Waveform card */}
            <Card className="flex-1 border border-default-100 shadow-none">
              <CardBody className="p-4">
                <WaveformPlayer
                  ref={waveformRef}
                  audioUrl={(downloadData as { download_url?: string } | undefined)?.download_url ?? null}
                  detections={detections}
                  getColorForType={getColorForType}
                  onTimeUpdate={setCurrentTime}
                  onDurationChange={setDuration}
                  onPlayingChange={setIsPlaying}
                  onRegionClick={handleDetectionClick}
                  onRegionHover={setHoveredDetection}
                  activeDetection={displayedActiveDetection}
                  height={160}
                  showTimeline
                />
              </CardBody>
            </Card>

            {/* Playback controls */}
            <Card className="border border-default-100 shadow-none">
              <CardBody className="p-4">
                <PlaybackControls
                  isPlaying={isPlaying}
                  currentTime={currentTime}
                  duration={duration}
                  playbackRate={playbackRate}
                  volume={volume}
                  zoom={zoomLevel}
                  disabled={!downloadData?.download_url}
                  onPlayPause={() => waveformRef.current?.playPause()}
                  onPlaybackRateChange={handlePlaybackRateChange}
                  onVolumeChange={handleVolumeChange}
                  onZoomChange={handleZoomChange}
                  onSeek={handleSeek}
                />
              </CardBody>
            </Card>
          </div>

          {/* Detection list section */}
          <div className="lg:w-[350px] flex flex-col gap-4">
            <Card className="flex-1 border border-default-100 shadow-none overflow-hidden">
              <CardHeader className="px-4 py-3 border-b border-default-100">
                <h3 className="text-sm font-medium">
                  Detections ({detections.length})
                </h3>
              </CardHeader>
              <CardBody className="p-2">
                <DetectionList
                  detections={detections}
                  activeDetection={displayedActiveDetection}
                  getColorForType={getColorForType}
                  onDetectionClick={handleDetectionClick}
                  onDetectionHover={setHoveredDetection}
                />
              </CardBody>
            </Card>

            {/* Keyboard shortcuts hint */}
            <Card className="border border-default-100 shadow-none">
              <CardBody className="p-3">
                <div className="flex items-center gap-2 text-xs text-default-500">
                  <Keyboard className="h-3.5 w-3.5" />
                  <span>Space: Play/Pause</span>
                  <span className="text-default-300">|</span>
                  <span>Arrows: Seek/Navigate</span>
                  <span className="text-default-300">|</span>
                  <span>Esc: Close</span>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// Header sub-component
function ViewerHeader({
  filename,
  detectionCount,
  onClose,
}: {
  filename?: string;
  detectionCount: number;
  onClose?: () => void;
}) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-default-100 bg-default-50">
      <div className="flex items-center gap-3">
        <FileAudio className="h-5 w-5 text-default-500" />
        <span className="font-medium truncate max-w-[400px]">
          {filename || 'Loading...'}
        </span>
        {detectionCount > 0 && (
          <Chip size="sm" variant="flat" color="warning">
            {detectionCount} {detectionCount === 1 ? 'detection' : 'detections'}
          </Chip>
        )}
      </div>
      {onClose && (
        <Button
          isIconOnly
          variant="light"
          size="sm"
          onPress={onClose}
          aria-label="Close viewer"
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
